import React from "react";
import { ArrowUpRight, Stethoscope, ShieldAlert } from "lucide-react";

/**
 * MedicalReferralBand
 * Reusable "lifestyle ↔ medical handoff" band.
 * Used on track pages where a medical-side service is the right destination
 * for clinical needs (hormone testing/TRT/HRT, deeper diagnostics, etc).
 */
export function MedicalReferralBand({ medicalPartner }) {
  if (!medicalPartner) return null;
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="border border-[color:var(--iw-blue)]/50 bg-[color:var(--iw-bg)] p-7 sm:p-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center lg:gap-12">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3">
                <span className="inline-grid h-11 w-11 place-items-center border border-[color:var(--iw-blue)]/70 bg-white text-[color:var(--iw-blue)]">
                  <Stethoscope className="h-5 w-5" />
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)]">
                  Medical Partner
                </span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold leading-tight tracking-tight text-[color:var(--iw-primary)] sm:text-3xl">
                {medicalPartner.headline}
              </h3>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-[color:var(--iw-body)] sm:text-lg">
                {medicalPartner.body}
              </p>
            </div>
            <div className="flex items-end lg:col-span-4 lg:justify-end">
              <a
                href={medicalPartner.cta.href}
                target="_blank"
                rel="noreferrer"
                className="iw-btn-ghost inline-flex items-center gap-2 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em]"
              >
                {medicalPartner.cta.label} <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * SelfRouteCard
 * Sits near the intake on pages where the wellness intake is NOT the right
 * starting point for someone seeking medical-side care. Self-route nudge.
 */
export function SelfRouteCard({ selfRouteCard }) {
  if (!selfRouteCard) return null;
  return (
    <section className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-primary)] py-10 text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
          <div className="flex items-start gap-4">
            <span className="mt-0.5 inline-grid h-11 w-11 flex-shrink-0 place-items-center border border-[color:var(--iw-accent-red)]/70 bg-white/10 text-[color:var(--iw-accent-red)] backdrop-blur">
              <ShieldAlert className="h-5 w-5" />
            </span>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)]">
                Before you apply
              </div>
              <h4 className="mt-2 font-display text-lg font-semibold leading-tight tracking-tight sm:text-xl">
                {selfRouteCard.title}
              </h4>
              <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
                {selfRouteCard.body}
              </p>
            </div>
          </div>
          <a
            href={selfRouteCard.cta.href}
            target="_blank"
            rel="noreferrer"
            className="iw-btn-red inline-flex flex-shrink-0 items-center gap-2 px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em]"
          >
            {selfRouteCard.cta.label} <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default MedicalReferralBand;
